import React, { useState, useEffect, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useDAppState, dappToast } from '../../context/DAppStateProvider';
import { shortenAddress } from '@cai/shared/utils/common';
import { useMintLayout } from './CvTopRail';

/** Shared header chrome for the CV marketplace layout. */

const MOBILE_ITEMS = [
  { k: '家', t: 'Home', path: '/' },
  { k: '市', t: 'Collection', path: '/collection' },
  { k: '造', t: 'Create', path: '/create' },
  { k: '財', t: 'Wallet', path: '/my-nft' },
  { k: '道', t: 'Roadmap', path: '/about#roadmap' },
  { k: '鎖', t: 'Networks', path: '/about#networks' },
  { k: '問', t: 'FAQ', path: '/about#faq' },
];

const NAV_ITEMS = [
  { label: 'COLLECTION', path: '/collection' },
  { label: 'CREATE', path: '/create' },
  { label: 'WALLET', path: '/my-nft' },
  { label: 'ROADMAP', path: '/about#roadmap' },
  { label: 'NETWORKS', path: '/about#networks' },
  { label: 'FAQ', path: '/about#faq' },
];

const isActive = (pathname, hash, itemPath) => {
  const [path, itemHash] = itemPath.split('#');
  if (itemHash) return pathname === path && hash === `#${itemHash}`;
  if (path === '/collection') return pathname === '/collection';
  if (path === '/') return pathname === '/' && !hash;
  return pathname === path || pathname.startsWith(path + '/');
};

export const CvWalletButton = () => {
  const {
    walletConnection,
    ConnectionState,
    setIsWalletModalOpen,
    handleDisconnect,
    isWalletAuthenticated,
    isWalletSigningIn,
    activeState,
  } = useDAppState();
  const [menuOpen, setMenuOpen] = useState(false);
  const connecting = walletConnection.connectionState === ConnectionState.Connecting;
  const address = walletConnection.address;
  const walletName = walletConnection.wallet;
  const balanceLabel =
    activeState?.balance != null && activeState.balance !== ''
      ? `${activeState.balance} ${activeState.symbol || ''}`.trim()
      : null;

  const copyAddress = useCallback(async () => {
    if (!address) return;
    try {
      await navigator.clipboard.writeText(address);
      dappToast.success('Address copied');
      setMenuOpen(false);
    } catch {
      dappToast.error('Failed to copy address');
    }
  }, [address]);

  useEffect(() => {
    const close = (e) => {
      if (!e.target.closest?.('.cvw-wrap')) setMenuOpen(false);
    };
    document.addEventListener('click', close);
    return () => document.removeEventListener('click', close);
  }, []);

  if (connecting) {
    return (
      <button className="cvw" disabled>
        <span className="cvw__sp" />
        {walletName ? `OPENING ${walletName.toUpperCase()}…` : 'OPENING WALLET…'}
      </button>
    );
  }

  if (isWalletSigningIn) {
    return (
      <button className="cvw" disabled>
        <span className="cvw__sp" />
        {walletName ? `SIGN IN ${walletName.toUpperCase()}…` : 'SIGN IN WALLET…'}
      </button>
    );
  }

  if (isWalletAuthenticated && address) {
    return (
      <span className="cvw-wrap">
        <button
          className="cvw cvw--on"
          aria-label="Wallet menu"
          onClick={(e) => {
            e.stopPropagation();
            setMenuOpen((o) => !o);
          }}
        >
          <span className="cvw__dot" />
          <span className="cvw__meta">
            <span className="cvw__addr-short">{shortenAddress(address)} ▾</span>
            {balanceLabel ? <span className="cvw__balance">{balanceLabel}</span> : null}
          </span>
        </button>
        <span className={`cvw-menu${menuOpen ? ' open' : ''}`}>
          <span className="cvw-addr">
            {address}
            {balanceLabel ? <span className="cvw-bal">{balanceLabel}</span> : null}
          </span>
          <button type="button" onClick={copyAddress}>
            ⧉ Copy address
          </button>
          <Link to="/profile" onClick={() => setMenuOpen(false)}>
            ◫ Profile
          </Link>
          <Link to="/my-nft" onClick={() => setMenuOpen(false)}>
            ◫ My wallet
          </Link>
          <Link to="/dashboard" onClick={() => setMenuOpen(false)}>
            ◫ Dashboard
          </Link>
          <button
            type="button"
            className="danger"
            onClick={() => {
              handleDisconnect();
              setMenuOpen(false);
            }}
          >
            ⏻ Disconnect
          </button>
        </span>
      </span>
    );
  }

  return (
    <button className="cvw" onClick={() => setIsWalletModalOpen(true)}>
      🦊 CONNECT
    </button>
  );
};

export const CvMobileNav = ({ onConnect }) => {
  const [open, setOpen] = useState(false);
  const {
    walletConnection,
    ConnectionState,
    setIsWalletModalOpen,
    isWalletAuthenticated,
    isWalletSigningIn,
  } = useDAppState();
  const connecting = walletConnection.connectionState === ConnectionState.Connecting;
  const address = walletConnection.address;
  const walletName = walletConnection.wallet;

  const close = () => {
    setOpen(false);
    document.body.style.overflow = '';
  };

  const openMenu = () => {
    setOpen(true);
    document.body.style.overflow = 'hidden';
  };

  return (
    <>
      <button className="cvburger" aria-label="Open menu" onClick={openMenu}>
        ☰
      </button>
      <div
        className={`cvmenu${open ? ' open' : ''}`}
        onClick={(e) => e.target === e.currentTarget && close()}
      >
        <div className="cvhead">
          <span className="cvlogo">CAI</span>
          <button className="x" aria-label="Close" onClick={close}>
            ✕
          </button>
        </div>
        {MOBILE_ITEMS.map((item) => (
          <Link key={item.path} className="row" to={item.path} onClick={close}>
            <span className="k">{item.k}</span>
            {item.t}
            <span className="ar">→</span>
          </Link>
        ))}
        <button
          className="row cta"
          onClick={(e) => {
            e.preventDefault();
            close();
            if (isWalletAuthenticated) onConnect?.();
            else setIsWalletModalOpen(true);
          }}
        >
          {connecting
            ? walletName
              ? `OPENING ${walletName.toUpperCase()}…`
              : 'OPENING WALLET…'
            : isWalletSigningIn
              ? walletName
                ? `SIGN IN ${walletName.toUpperCase()}…`
                : 'SIGN IN WALLET…'
              : isWalletAuthenticated && address
                ? `● ${shortenAddress(address)}`
                : '🦊 CONNECT WALLET'}
          <span className="ar">→</span>
        </button>
      </div>
    </>
  );
};

const CvHeader = () => {
  const { pathname, hash } = useLocation();
  const mintLayout = useMintLayout();

  return (
    <header className="nav">
      <div className={mintLayout ? 'wrap--mint' : 'wrap'}>
        <Link className="logo" to="/">
          CAI<small>{mintLayout ? 'Cryptoverse' : 'CRYPTOVERSE-AI'}</small>
        </Link>
        <nav className="topnav">
          {NAV_ITEMS.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              className={isActive(pathname, hash, item.path) ? 'on' : ''}
            >
              {item.label}
            </Link>
          ))}
        </nav>
        <div className="sp" />
        <CvWalletButton />
        <CvMobileNav />
      </div>
    </header>
  );
};

export default CvHeader;
